import { Bug, Target, HeartPulse, HelpCircle } from "lucide-react";
import { LUNG_ILLUSTRATION, waLink } from "../../lib/constants";
import useReveal from "../../hooks/useReveal";

const CAUSAS = [
  {
    slug: "infeccoes",
    title: "Infecções",
    badge: "Exsudato / Empiema",
    icon: Bug,
    color: "#2E7D32",
    bg: "rgba(46, 125, 50, 0.08)",
    text:
      "Pneumonia bacteriana, tuberculose, fungos ou infecções pulmonares por outros microorganismos podem causar derrame como consequência de uma reação inflamatória. O líquido costuma ser definido como exsudato (rico em proteínas) e às vezes infectado (empiema). Tratamento: antibióticos + drenagem quando necessário.",
  },
  {
    slug: "tumores",
    title: "Tumores",
    badge: "Derrame Maligno",
    icon: Target,
    color: "#C62828",
    bg: "rgba(198, 40, 40, 0.08)",
    text:
      "Câncer de pulmão, mama, estômago ou outros tumores podem formar metástase para a pleura e desenvolver um Derrame Pleural Maligno. Tratamento: diagnóstico e tratamento do tumor primário + drenagem quando necessário.",
  },
  {
    slug: "insuficiencia-cardiaca",
    title: "Insuficiência Cardíaca",
    badge: "Causa mais comum",
    icon: HeartPulse,
    color: "#0D47A1",
    bg: "rgba(13, 71, 161, 0.08)",
    text:
      "Coração fraco não consegue bombear sangue adequadamente, aumentando pressão venosa e causando derrame pleural (análise do líquido pleural revela transudato). É a causa mais comum de derrame pleural. Tratamento: tratamento da causa de base (controle da pressão arterial, evitar congestão) + drenagem quando necessário.",
  },
  {
    slug: "outras",
    title: "Outras Causas",
    badge: "Investigação específica",
    icon: HelpCircle,
    color: "#546E7A",
    bg: "rgba(84, 110, 122, 0.10)",
    text:
      "Desnutrição, doenças autoimunes, cirrose hepática, insuficiência renal, embolia pulmonar, trauma, pancreatite, hipotiroidismo, pós-operatório, entre outras. Cada causa tem abordagem específica e o diagnóstico diferencial é crucial para tratamento adequado.",
  },
];

export default function WhatIs() {
  const { ref, isVisible } = useReveal();

  return (
    <section
      id="oquee"
      ref={ref}
      className={`py-24 md:py-32 bg-[#EEF4FF] reveal-section ${isVisible ? "is-visible" : ""}`}
      data-testid="whatis-section"
    >
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div className="inline-flex items-center bg-[#0D47A1]/10 text-[#0D47A1] rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
            Entenda Sem Pânico
          </div>
          <h2 className="font-poppins font-bold text-3xl sm:text-4xl text-[#212121] mb-6 leading-tight">
            O Que É Derrame Pleural?
          </h2>
          <p className="text-base md:text-lg text-[#212121]/70 leading-relaxed">
            O Derrame Pleural muitas vezes gera ansiedade porque parece um problema grave. Na realidade,
            derrame pleural é uma condição tratável, e a maioria dos casos têm ótimo prognóstico com
            manejo adequado. Entenda o que você tem e por que precisa de um especialista.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-16">
          <div className="flex items-center justify-center">
            <img
              src={LUNG_ILLUSTRATION}
              alt="Ilustração anatômica: pulmão comprimido e derrame pleural"
              loading="lazy"
              className="w-full h-full max-h-[560px] object-contain"
              data-testid="lung-illustration"
            />
          </div>
          <div className="bg-white rounded-3xl p-8 md:p-10 shadow-sm border border-gray-100 flex flex-col justify-center">
            <h3 className="font-poppins font-bold text-xl md:text-2xl text-[#0D47A1] mb-4">
              Definição
            </h3>
            <p className="text-[#212121]/75 leading-relaxed mb-4 text-base md:text-lg">
              Derrame pleural é o acúmulo anormal de líquido entre a pleura visceral (que cobre o pulmão)
              e a pleura parietal (que reveste a parede torácica).
            </p>
            <p className="text-[#212121]/75 leading-relaxed mb-4 text-base md:text-lg">
              O líquido pleural existe em condições normais, mas apenas em uma mínima quantidade (cerca
              de 5–15 ml) que funciona como lubrificante. Ele permite que o pulmão se movimente e
              expanda livremente durante a respiração.
            </p>
            <p className="text-[#212121]/75 leading-relaxed mb-4 text-base md:text-lg">
              Quando essa quantidade aumenta além do normal, temos um Derrame Pleural. Pode ser leve
              (pequeno volume, poucos sintomas), moderado ou volumoso (grande volume, sintomas
              respiratórios intensos).
            </p>
            <p className="text-[#00897B] leading-relaxed font-semibold text-base md:text-lg">
              A boa notícia: derrame pleural é um sinal de que algo precisa de atenção, mas na maioria
              dos casos é tratável.
            </p>
          </div>
        </div>

        <div className="text-center mb-10">
          <h3 className="font-poppins font-bold text-2xl md:text-3xl text-[#212121] mb-3">
            Principais Causas de Derrame Pleural
          </h3>
          <p className="text-[#212121]/65 max-w-2xl mx-auto">
            Derrame pleural sempre tem uma causa. Identificar a causa é essencial para o tratamento
            correto. As causas mais frequentes são:
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {CAUSAS.map((c) => {
            const Icon = c.icon;
            return (
              <div
                key={c.slug}
                data-testid={`causa-card-${c.slug}`}
                className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ backgroundColor: c.bg }}
                >
                  <Icon size={24} style={{ color: c.color }} />
                </div>
                <h4 className="font-poppins font-bold text-lg text-[#212121] mb-2">{c.title}</h4>
                <span
                  className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
                  style={{ backgroundColor: c.bg, color: c.color }}
                >
                  {c.badge}
                </span>
                <p className="text-[#212121]/70 leading-relaxed text-sm">{c.text}</p>
              </div>
            );
          })}
        </div>

        <div className="text-center">
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatis-cta-button"
            className="inline-flex items-center gap-2 bg-[#0D47A1] text-white font-poppins font-bold px-10 py-4 rounded-full hover:bg-[#00897B] transition-colors duration-200 shadow-lg hover:-translate-y-0.5 hover:shadow-xl"
          >
            AGENDAR AVALIAÇÃO
          </a>
        </div>
      </div>
    </section>
  );
}
